import { useState } from 'react';

interface MaturityLevel {
  level: number;
  title: string;
  subtitle: string;
  characteristics: string;
  risk: string;
  problems: string[];
  actions: string[];
}

const maturityLevels: MaturityLevel[] = [
  {
    level: 1,
    title: '分散与人治',
    subtitle: 'Level 1',
    characteristics: '资料分散、流程依赖个人',
    risk: '口径不一致、问题不可复盘',
    problems: [
      '单证散落在不同文件夹和系统，查找困难',
      '业务流程依赖个人经验，人员变动风险高',
      '缺乏统一的数据口径和标准',
      '问题发生后难以追溯原因',
    ],
    actions: [
      '建立统一的资料归集机制',
      '制定标准化的业务流程',
      '引入工具辅助提升效率',
    ],
  },
  {
    level: 2,
    title: '工具辅助',
    subtitle: 'Level 2',
    characteristics: '能跑通单票、局部自动化',
    risk: '留痕不系统、规模增长后失控',
    problems: [
      '单票业务可以完成，但缺乏系统性留痕',
      '局部环节自动化，整体链路未打通',
      '规模增长后工具能力无法支撑',
      '数据分散在多个工具中，难以统一管理',
    ],
    actions: [
      '建立系统化的留痕机制',
      '打通业务全链路数据',
      '引入校验与归档能力',
    ],
  },
  {
    level: 3,
    title: '系统化治理',
    subtitle: 'Level 3',
    characteristics: '归集+校验+归档形成机制',
    risk: '返工减少、资料可追溯',
    problems: [
      '基本建立了归集、校验、归档机制',
      '但链路关系尚未完全固化',
      '跨部门协作仍有口径不一致情况',
      '审计准备度有待提升',
    ],
    actions: [
      '固化一票一链路结构',
      '建立权限与责任体系',
      '完善归档目录化管理',
    ],
  },
  {
    level: 4,
    title: '能力化合规',
    subtitle: 'Level 4',
    characteristics: '一票一链路、责任与权限清晰、审计准备度高',
    risk: '合规成为日常管理的一部分',
    problems: [
      '已建立完整的合规治理体系',
      '持续优化和提升治理质量',
      '探索更高效的管理方式',
    ],
    actions: [
      '持续优化治理机制',
      '沉淀最佳实践',
      '提升团队能力',
    ],
  },
];

export default function GovernanceMaturity() {
  const [expandedLevel, setExpandedLevel] = useState<number | null>(null);

  const toggleLevel = (level: number) => {
    setExpandedLevel(expandedLevel === level ? null : level);
  };

  return (
    <section className="py-16 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-[1400px] mx-auto px-6">
        {/* 标题 */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            合规治理成熟度
          </h2>
          <p className="text-lg text-gray-600">
            从分散管理到能力化管理
          </p>
        </div>

        {/* 成熟度阶梯 */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          {maturityLevels.map((item) => {
            const isExpanded = expandedLevel === item.level;
            const isHighLevel = item.level >= 3;

            return (
              <button
                key={item.level}
                onClick={() => toggleLevel(item.level)}
                className={`relative p-6 rounded-2xl border-2 transition-all cursor-pointer text-left ${
                  isExpanded
                    ? 'border-teal-500 bg-teal-50 shadow-lg scale-105'
                    : 'border-gray-200 bg-white hover:border-teal-200 hover:shadow-md'
                }`}
              >
                {/* Level 标签 */}
                <div
                  className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-semibold mb-4 ${
                    isHighLevel
                      ? 'bg-teal-100 text-teal-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {item.subtitle}
                </div>

                {/* 标题 */}
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {item.title}
                </h3>

                {/* 特征 */}
                <div className="mb-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                    特征
                  </p>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {item.characteristics}
                  </p>
                </div>

                {/* 风险/结果 */}
                <div className="mb-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                    {isHighLevel ? '结果' : '风险'}
                  </p>
                  <p
                    className={`text-sm leading-relaxed ${
                      isHighLevel ? 'text-teal-700 font-medium' : 'text-red-600'
                    }`}
                  >
                    {item.risk}
                  </p>
                </div>

                {/* 展开指示 */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <span className="text-xs text-gray-500">
                    {isExpanded ? '收起详情' : '查看详情'}
                  </span>
                  <i
                    className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line text-gray-400 transition-transform`}
                  ></i>
                </div>
              </button>
            );
          })}
        </div>

        {/* 展开内容 */}
        {expandedLevel !== null && (
          <div className="bg-white rounded-2xl border-2 border-teal-200 p-8 shadow-lg">
            <div className="grid md:grid-cols-2 gap-8">
              {/* 典型问题 */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-100">
                    <i className="ri-error-warning-line text-red-600"></i>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900">典型问题</h4>
                </div>
                <ul className="space-y-3">
                  {maturityLevels[expandedLevel - 1].problems.map((problem, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center rounded-full bg-red-50 text-red-600 flex-shrink-0 mt-0.5">
                        <i className="ri-close-line text-xs"></i>
                      </div>
                      <span className="text-sm text-gray-700 leading-relaxed">
                        {problem}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* 建议动作 */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-teal-100">
                    <i className="ri-lightbulb-line text-teal-600"></i>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900">建议动作</h4>
                </div>
                <ul className="space-y-3">
                  {maturityLevels[expandedLevel - 1].actions.map((action, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center rounded-full bg-teal-100 text-teal-600 flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-xs"></i>
                      </div>
                      <span className="text-sm text-gray-700 leading-relaxed">
                        {action}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* 底部说明 */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-600 leading-relaxed">
            星贸云覆盖 Level 2–4 的治理升级路径，先把"口径与证据链"做成系统能力。
          </p>
        </div>
      </div>
    </section>
  );
}